import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  User,
  Settings as SettingsIcon,
  LogOut,
  Loader2,
  Save,
  Globe,
  MapPin,
  Crown,
  Shield,
  Ticket,
  Check,
  Sparkles,
  Mail,
  AlertCircle,
  BadgeCheck,
  CreditCard,
  ShieldCheck,
  Trash2,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES } from '../i18n';
import PromoCodeInput from '../components/PromoCodeInput';
import NotificationSettings from '../components/settings/NotificationSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countries = [
  { code: 'NONE', name: 'Select Country', flag: '🌍' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'RU', name: 'Russia', flag: '🇷🇺' },
  { code: 'OTHER', name: 'Other', flag: '🌍' }
];

const memberClasses = [
  { value: 'guest', label: 'Guest', icon: User, color: 'bg-gray-100 text-gray-700', desc: 'Free access to basic features' },
  { value: 'paid', label: 'Full Member', icon: Crown, color: 'bg-amber-100 text-amber-700', desc: 'Full access to all community features' },
  { value: 'volunteer', label: 'Volunteer', icon: Shield, color: 'bg-green-100 text-green-700', desc: 'Community volunteer with special privileges' },
  { value: 'breeder', label: 'Breeder', icon: Crown, color: 'bg-purple-100 text-purple-700', desc: 'Verified dog breeder' },
  { value: 'professional', label: 'Professional', icon: Shield, color: 'bg-blue-100 text-blue-700', desc: 'Dog care professional (trainer, vet, etc.)' },
  { value: 'vendor', label: 'Vendor', icon: Crown, color: 'bg-orange-100 text-orange-700', desc: 'Business selling dog products or services' }
];

// Membership tiers with pricing
const membershipTiers = [
  { 
    id: 'free', 
    name: 'Free', 
    price: 0, 
    features: ['Access to public forums', 'Create and join groups', 'Basic pet profiles'],
    color: 'bg-gray-100 text-gray-700'
  },
  { 
    id: 'paid', 
    name: 'Full Member', 
    price: 5.99, 
    features: ['Ad-free experience', 'Priority support', 'Extended pet profiles', 'Access to member deals'],
    color: 'bg-amber-100 text-amber-700'
  },
  { 
    id: 'premium', 
    name: 'Premium Professional', 
    price: 14.99, 
    features: ['Professional badge', 'Private professional forums', 'Business listing', 'Voting in board elections'],
    color: 'bg-purple-100 text-purple-700'
  }
];

// Membership Upgrade Section Component
const MembershipUpgradeSection = ({ user, onUpgrade }) => {
  const navigate = useNavigate();
  
  const currentTier = user?.membership_tier || 'free';
  const currentTierInfo = membershipTiers.find(t => t.id === currentTier) || membershipTiers[0];

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Label className="text-sm font-medium">Membership Tier</Label>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={currentTierInfo.color}>{currentTierInfo.name}</Badge>
            {user?.membership_expires_at && (
              <span className="text-xs text-muted-foreground">
                Expires: {new Date(user.membership_expires_at).toLocaleDateString()}
              </span>
            )}
            {user?.beta_membership_active && (
              <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                Beta Benefits
              </Badge>
            )}
          </div>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate('/membership/upgrade')}
          data-testid="upgrade-membership-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {currentTier === 'free' ? 'Upgrade' : 'Manage Plan'}
        </Button>
      </div>
      
      {/* Quick info about current plan */}
      <div className="text-xs text-muted-foreground">
        {currentTier === 'free' ? (
          <p>Upgrade to unlock ad-free experience, voting rights, and more.</p>
        ) : currentTier === 'paid' ? (
          <p>You have full club membership benefits. Upgrade to Premium for professional features.</p>
        ) : (
          <p>You have access to all premium features including ad-free experience and professional forums.</p>
        )}
      </div>
    </div>
  );
};

// Identity Verification Section Component
const IdentityVerificationSection = ({ user, token, onVerified }) => {
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/identity/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching verification status:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchStatus();
  }, [token]);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API}/payments/identity/start`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      
      if (data.verification_type === 'stripe_identity' && data.url) {
        // Stripe Identity — redirect to Stripe's hosted verification page
        window.location.href = data.url;
      } else if (data.checkout_url) {
        // Card verification fallback
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Already verified');
      } else {
        toast.error('Failed to start verification. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading verification status...</span>
        </div>
      </div>
    );
  }

  const methodLabel = status?.verification_method === 'stripe_identity_document'
    ? 'document verification'
    : 'card authorization';

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Label className="text-sm font-medium flex items-center gap-2">
            <BadgeCheck className="w-4 h-4" />
            Identity Verification
          </Label>
          <div className="flex items-center gap-2 mt-1">
            {status?.identity_verified ? (
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" /> Verified
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                Not Verified
              </Badge>
            )}
            {status?.verified_at && (
              <span className="text-xs text-muted-foreground">
                Since: {new Date(status.verified_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        
        {!status?.identity_verified && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleVerify}
            disabled={verifying}
            data-testid="verify-identity-btn"
          >
            {verifying ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
            ) : (
              <><ShieldCheck className="w-4 h-4 mr-2" /> Get Verified</>
            )}
          </Button>
        )}
      </div>
      
      <div className="text-xs text-muted-foreground space-y-1">
        {status?.identity_verified ? (
          <p className="text-green-700">Your identity has been verified via {methodLabel}. This adds trust to your profile.</p>
        ) : (
          <>
            <p>Verify your identity to add a trusted badge to your profile. Verification uses Stripe's secure platform — your personal documents and card details are handled entirely by Stripe and never touch our servers.</p>
          </>
        )}
      </div>
    </div>
  );
};

// Account Deletion Section Component
const AccountDeletionSection = ({ user, token }) => {
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deletionPending, setDeletionPending] = useState(!!user?.deletion_requested);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/privacy/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deletion request submitted. Your account will be deleted within 30 days.');
      setShowConfirm(false);
      setDeletionPending(true);
    } catch (error) {
      toast.error('Failed to submit deletion request. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/privacy/data/cancel-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deletion request cancelled. Your account is safe.');
      setDeletionPending(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel deletion request.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-4">
      {deletionPending ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3" data-testid="deletion-pending-banner">
          <p className="text-sm text-amber-800 font-medium">
            Account deletion is scheduled. Your account and all data will be permanently removed within 30 days.
          </p>
          <p className="text-xs text-amber-600">
            Changed your mind? You can cancel the deletion request below.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={handleCancelDeletion}
            disabled={cancelling}
            data-testid="cancel-deletion-btn"
          >
            {cancelling ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling...</>
            ) : (
              'Cancel Deletion Request'
            )}
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Request to permanently delete your account and all associated data. You have 30 days to change your mind.
          </p>
          
          {!showConfirm ? (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowConfirm(true)}
              data-testid="request-deletion-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Request Account Deletion
            </Button>
          ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
              <p className="text-sm text-red-700 font-medium">
                Are you sure? After 30 days this will permanently delete:
              </p>
              <ul className="text-xs text-red-600 list-disc list-inside space-y-1">
                <li>Your profile and settings</li>
                <li>All your pets and their records</li>
                <li>Your posts, comments, and messages</li>
                <li>Any payment history</li>
              </ul>
              <p className="text-xs text-gray-500">You can cancel within 30 days from Settings.</p>
              <div className="flex gap-2">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                'Yes, Delete My Account'
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

// Beta Status Toggle Component
const BetaStatusToggle = ({ user, token, onToggle }) => {
  const [toggling, setToggling] = useState(false);
  const [betaStatus, setBetaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [betaCode, setBetaCode] = useState('');

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/membership/beta-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBetaStatus(response.data);
      } catch (error) {
        console.error('Error fetching beta status:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchStatus();
  }, [token]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const response = await axios.post(
        `${API}/membership/toggle-beta-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setBetaStatus(prev => ({ ...prev, is_beta_user: response.data.is_beta_user }));
      onToggle?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle beta status');
    } finally {
      setToggling(false);
    }
  };

  const handleActivate = async () => {
    if (!betaCode.trim()) {
      toast.error('Please enter the beta access code');
      return;
    }
    setActivating(true);
    try {
      const response = await axios.post(
        `${API}/membership/activate-beta?beta_code=${encodeURIComponent(betaCode)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setBetaStatus(prev => ({ ...prev, is_beta_user: true, can_toggle: true }));
      setBetaCode('');
      onToggle?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate beta status');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading beta status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Label className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Beta Tester Status
          </Label>
          <div className="flex items-center gap-2 mt-1">
            {betaStatus?.is_beta_user ? (
              <Badge className="bg-amber-100 text-amber-700">Beta Active</Badge>
            ) : betaStatus?.can_toggle ? (
              <Badge variant="outline" className="text-gray-500">Beta Paused</Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400">Not Activated</Badge>
            )}
          </div>
        </div>

        {betaStatus?.can_toggle ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggle}
            disabled={toggling}
            data-testid="toggle-beta-btn"
          >
            {toggling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : betaStatus?.is_beta_user ? (
              'Pause Beta'
            ) : (
              'Resume Beta'
            )}
          </Button>
        ) : null}
      </div>

      {/* Activation form for users who haven't activated yet */}
      {!betaStatus?.can_toggle && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 mb-2">
            Enter your beta access code to activate beta tester features:
          </p>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Enter beta code"
              value={betaCode}
              onChange={(e) => setBetaCode(e.target.value)}
              className="flex-1"
              data-testid="beta-code-input"
            />
            <Button
              size="sm"
              onClick={handleActivate}
              disabled={activating}
              data-testid="activate-beta-btn"
            >
              {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate'}
            </Button>
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground mt-2">
        {betaStatus?.can_toggle ? (
          <p>
            {betaStatus?.is_beta_user 
              ? "You have full beta access. Pause to test how the site works for regular users."
              : "Beta paused. You're viewing the site as a regular user. Resume to access beta features."
            }
          </p>
        ) : (
          <p>Beta testers get early access to new features and can submit feedback to help improve the platform.</p>
        )}
      </div>
    </div>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, updateUser, logout, refreshUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [formData, setFormData] = useState({
    display_name: user?.display_name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    country: user?.country || 'NONE',
    avatar_url: user?.avatar_url || '',
    preferred_language: user?.preferred_language || i18n.language || 'en',
    member_class: user?.member_class || 'guest'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        display_name: user.display_name || '',
        bio: user.bio || '',
        location: user.location || '',
        country: user.country || 'NONE',
        avatar_url: user.avatar_url || '',
        preferred_language: user.preferred_language || i18n.language || 'en',
        member_class: user.member_class || 'guest'
      });
    }
  }, [user, i18n.language]);

  const handleLanguageChange = (langCode) => {
    setFormData({ ...formData, preferred_language: langCode });
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.put(`${API}/users/profile`, formData);
      updateUser(response.data);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out');
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const response = await axios.post(
        `${API}/auth/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verification email sent! Check your inbox.');
      if (refreshUser) refreshUser();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a few minutes.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send verification email');
      }
    } finally {
      setResendingVerification(false);
    }
  };

  const currentMemberClass = memberClasses.find(m => m.value === formData.member_class) || memberClasses[0];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-heading font-medium mb-8 flex items-center gap-3">
            <SettingsIcon className="w-8 h-8" />
            {t('settings.title')}
          </h1>

          {/* Email Verification Status */}
          {user && !user.email_verified && (
            <Card className="mb-6 border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-amber-800">Verify your email</h3>
                    <p className="text-sm text-amber-700 mb-3">
                      Please verify <span className="font-medium">{user.email}</span> to unlock all features.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      {resendingVerification ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" /> Resend Verification Email</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profile Settings */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('settings.profile')}
                {user?.email_verified && (
                  <Badge variant="outline" className="text-green-600 border-green-300 ml-2">
                    <Check className="w-3 h-3 mr-1" /> Email Verified
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Avatar Preview */}
                <div className="flex items-center gap-4">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={formData.avatar_url} />
                    <AvatarFallback className="text-2xl">
                      {formData.display_name?.charAt(0) || user?.username?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <Label htmlFor="avatar_url">Avatar URL</Label>
                    <Input
                      id="avatar_url"
                      placeholder="https://..."
                      value={formData.avatar_url}
                      onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    placeholder="Your name"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="input-field"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell us about yourself and your dogs..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Location
                    </Label>
                    <Input
                      id="location"
                      placeholder="City"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="input-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('settings.country')}</Label>
                    <Select value={formData.country} onValueChange={(val) => setFormData({ ...formData, country: val })}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('settings.selectCountry')}>
                          {countries.find(c => c.code === formData.country)?.flag}{' '}
                          {countries.find(c => c.code === formData.country)?.name || t('settings.selectCountry')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.saveChanges')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Language & Preferences */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Globe className="w-5 h-5" />
                {t('settings.preferences')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{t('settings.language')}</Label>
                <p className="text-sm text-muted-foreground mb-2">{t('settings.selectLanguage')}</p>
                <Select value={formData.preferred_language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue>
                      {SUPPORTED_LANGUAGES.find(l => l.code === formData.preferred_language)?.flag}{' '}
                      {SUPPORTED_LANGUAGES.find(l => l.code === formData.preferred_language)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Push Notifications */}
          <div className="mb-6">
            <NotificationSettings />
          </div>

          {/* Membership */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Crown className="w-5 h-5" />
                {t('settings.memberClass')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Badge className={`${currentMemberClass.color} text-sm px-3 py-1`}>
                  {t(`memberClasses.${currentMemberClass.value}`)}
                </Badge>
                <p className="text-sm text-muted-foreground">{t(`memberClasses.${currentMemberClass.value}Desc`)}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Change Membership</Label>
                <Select value={formData.member_class} onValueChange={(val) => setFormData({ ...formData, member_class: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {memberClasses.map((mc) => (
                      <SelectItem key={mc.value} value={mc.value}>
                        <span className="flex items-center gap-2">
                          <Badge className={`${mc.color} text-xs`}>{t(`memberClasses.${mc.value}`)}</Badge>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Membership Tier Upgrade */}
              <MembershipUpgradeSection user={user} onUpgrade={refreshUser} />
              
              {/* Beta Status Toggle */}
              <BetaStatusToggle user={user} token={token} onToggle={refreshUser} />
              
              {/* Identity Verification */}
              <IdentityVerificationSection user={user} token={token} onVerified={refreshUser} />
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Username</Label>
                <p className="font-medium">@{user?.username}</p>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="font-heading text-destructive">Danger Zone</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logout */}
              <div>
                <p className="text-muted-foreground mb-4">
                  Logging out will end your current session.
                </p>
                <Button 
                  variant="destructive" 
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </Button>
              </div>
              
              {/* Account Deletion */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium text-destructive mb-3 block">Delete Account</Label>
                <AccountDeletionSection user={user} token={token} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;
